import { faChevronRight } from '@fortawesome/pro-solid-svg-icons';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DefaultSplashV2 from '../components/DefaultSplashV2';
import GenericSignUpForm from '../components/Forms/GenericSignUpForm';
import HomeRoute from '../components/Helmet/HomeRoute';
import {
  TC_ONEREAL_DATA_POLICY_LINK,
  TC_ONEREAL_ELECTRONIC_DISCLOSURE_LINK,
  TC_ONEREAL_PRIVACY_POLICY_LINK,
  TC_ONEREAL_TERMS_OF_USE_LINK,
} from '../constants/OneRealMortgageConstants';
import {
  useOnboardingTracking,
  TeamInviteProperties,
} from '../hooks/useOnboardingTracking';
import useQueryParams from '../hooks/useQueryParams';
import { AuthControllerApi, LoginRequest } from '../openapi/keymaker';
import {
  PublicControllerApi,
  SignUpAndOnboardRequestRoleEnum,
  TeamInvitationDtoStatusEnum,
  TeamPublicControllerApi,
} from '../openapi/yenta';
import CampaignService from '../services/CampaignService';
import ErrorService from '../services/ErrorService';
import { fetchAuthUserDetail, signOutUser } from '../slices/AuthSlice';
import { useAppDispatch } from '../slices/store';
import { setAuthCookie } from '../utils/AuthUtils';
import { getCommonsApiErrorMessage } from '../utils/ErrorUtils';
import Logger from '../utils/Logger';
import {
  getKeymakerConfiguration,
  getYentaConfiguration,
} from '../utils/OpenapiConfigurationUtils';
import SponsorService from '../utils/SponsorUtils';

// Static consent labels - created once, outside component to prevent recreation on each render
const TERMS_LABEL = (
  <div>
    <p className='font-inter-light font-light text-xs text-reskin-grey-600'>
      By checking the box, I agree to the{' '}
      <a
        className='font-inter-bold font-bold'
        href={TC_ONEREAL_PRIVACY_POLICY_LINK}
        target='_blank'
        rel='noreferrer'
      >
        Privacy Policy
      </a>
      ,{' '}
      <a
        className='font-inter-bold font-bold'
        href={TC_ONEREAL_TERMS_OF_USE_LINK}
        target='_blank'
        rel='noreferrer'
      >
        Terms of Use
      </a>
      {' and '}
      <a
        className='font-inter-bold font-bold'
        href={TC_ONEREAL_ELECTRONIC_DISCLOSURE_LINK}
        target='_blank'
        rel='noreferrer'
      >
        Electronic Disclosure
      </a>
      {' and '}
      <a
        className='font-inter-bold font-bold'
        href={TC_ONEREAL_DATA_POLICY_LINK}
        target='_blank'
        rel='noreferrer'
      >
        Consent
      </a>
      .
    </p>
  </div>
);

const CALL_MESSAGE_LABEL = (
  <div>
    <p className='font-inter-light font-light text-xs text-reskin-grey-600'>
      By checking the applicable box below, I consent by electronic signature to
      receive calls and text messages from Real Broker, LLC and its affiliated
      entities, if the applicable box is checked, made by any means or
      technology, for informational, marketing, or any other purposes at the
      phone number I provided above. I understand that my consent to such calls
      and text messages is not required to purchase products from or use the
      services of any of the entities listed below.
    </p>
  </div>
);

// Static form field configuration - prevents recreation on each render
const FORM_FIELDS = {
  firstName: true,
  lastName: true,
  country: false, // Country field is not shown for ReZEN agents
  username: true,
  email: true,
  password: true,
  confirmPassword: true,
  termsConsent: true,
  callMessageConsent: true,
} as const;

// Base field configuration - prevents recreation on each render
const BASE_FIELD_CONFIG = {
  firstName: {
    placeholder: 'Enter your first name',
  },
  lastName: {
    placeholder: 'Enter your last name',
  },
  username: {
    placeholder: 'eg. johndoe9',
  },
  email: {
    placeholder: 'johndoe@example.com',
  },
  termsConsent: {
    label: TERMS_LABEL,
  },
  callMessageConsent: {
    label: CALL_MESSAGE_LABEL,
  },
} as const;

// Static layout configuration
const LAYOUT_CONFIG = {
  columns: 2 as 1 | 2,
  fullWidthFields: ['country'],
};

interface Query {
  rezenCampaign?: string;
  sponsorCode?: string;
  sponsorName?: string;
}

/**
 * ReZEN Agent sign-up route using GenericSignUpForm.
 * Registration form for ReZEN agents without the country field.
 */
const RezenAgentSignUpRoute: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [fetchingInvitation, setFetchingInvitation] = useState(false);
  const [submitError, setSubmitError] = useState<string>();
  const [isRevokedInvite, setIsRevokedInvite] = useState<boolean>(false);
  const { sponsorCode, sponsorName, rezenCampaign } = useQueryParams<Query>();
  const isSubmittingRef = useRef(false);
  const { trackSelfServeTeamInviteClicked } = useOnboardingTracking();

  // Capture and store rezenCampaign parameter when present
  useEffect(() => {
    if (rezenCampaign) {
      CampaignService.setCampaign(rezenCampaign);
    }
  }, [rezenCampaign]);

  // Save sponsor info to localStorage when present in URL
  useEffect(() => {
    if (sponsorCode || sponsorName) {
      SponsorService.saveSponsorInfo({ sponsorCode, sponsorName });
    }
  }, [sponsorCode, sponsorName]);

  // Extract invitation IDs from URL query params
  const teamInvitationId = searchParams.get('teamInvitationId');
  const genericTeamInvitationId = searchParams.get('genericTeamInvitationId');
  const teamId = searchParams.get('teamId');

  // State for invitation data
  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [onboardingEmail, setOnboardingEmail] = useState<string>('');
  const [teamInviteProps, setTeamInviteProps] =
    useState<TeamInviteProperties | null>(null);

  // Fetch team invitation data
  const fetchTeamInvitation = useCallback(async () => {
    if (!teamInvitationId) return;

    try {
      setFetchingInvitation(true);
      const { data } = await new TeamPublicControllerApi(
        await getYentaConfiguration(),
      ).getTeamInvitation(teamInvitationId);

      setFirstName(data.firstName || '');
      setLastName(data.lastName || '');
      setOnboardingEmail(data.emailAddress || '');
      setIsRevokedInvite(data.status === TeamInvitationDtoStatusEnum.Cancelled);

      // Store team invite properties for analytics
      if (data.teamId && data.invitationId) {
        setTeamInviteProps({
          team_invite_id: data.invitationId,
          team_id: data.teamId,
          invite_method: 'email', // teamInvitationId indicates email invite
        });
      }
    } catch (error) {
      Logger.error('Failed to fetch team invitation', error);
      ErrorService.notifyIgnoreHandled(
        'Failed to fetch team invitation',
        error as Error,
      );
    } finally {
      setFetchingInvitation(false);
    }
  }, [teamInvitationId]);

  // Fetch invitation data on mount
  useEffect(() => {
    fetchTeamInvitation();
  }, [fetchTeamInvitation]);

  // Set team invite props for generic (link) invitations
  useEffect(() => {
    if (genericTeamInvitationId) {
      setTeamInviteProps({
        team_invite_id: genericTeamInvitationId,
        team_id: teamId as string, // Team ID not available for generic invites at signup
        invite_method: 'link',
      });
    }
  }, [genericTeamInvitationId, teamId]);

  // Track team invite clicked when page loads with invitation
  useEffect(() => {
    if (teamInviteProps) {
      try {
        trackSelfServeTeamInviteClicked(
          'rezen_agent_signup_screen',
          teamInviteProps,
          undefined,
          {
            firstName,
            lastName,
            emailAddress: onboardingEmail,
          },
        );
      } catch (error) {
        Logger.error('Failed to track team invite clicked event', error);
      }
    }
  }, [
    teamInviteProps,
    trackSelfServeTeamInviteClicked,
    firstName,
    lastName,
    onboardingEmail,
  ]);

  // Username availability check - memoized to prevent recreation
  const checkUsernameAvailability = useCallback(
    async (username: string): Promise<boolean> => {
      try {
        const { data } = await new AuthControllerApi(
          await getKeymakerConfiguration(),
        ).checkUsernameAvailability(username);
        return Boolean(data?.available);
      } catch (e) {
        Logger.error('Username availability check failed', e);
        return false;
      }
    },
    [],
  );

  // Email availability check - memoized to prevent recreation
  const checkEmailAvailability = useCallback(
    async (email: string): Promise<boolean> => {
      try {
        const { data } = await new AuthControllerApi(
          await getKeymakerConfiguration(),
        ).checkEmailAvailability(email);
        return Boolean(data?.available);
      } catch (e) {
        Logger.error('Email availability check failed', e);
        return false;
      }
    },
    [],
  );

  // Handle form submission - memoized to prevent recreation
  const handleSubmit = useCallback(
    async (values: any) => {
      // Prevent duplicate submissions using ref for immediate access
      if (isSubmittingRef.current) {
        return;
      }

      isSubmittingRef.current = true;
      setLoading(true);
      setSubmitError(undefined);

      try {
        // Sign up with Agent role, including invitation IDs if present
        await new PublicControllerApi(
          await getYentaConfiguration(),
        ).userSignupWithOnboarding({
          role: SignUpAndOnboardRequestRoleEnum.Agent,
          firstName: values.firstName,
          lastName: values.lastName,
          username: values.username,
          emailAddress: values.emailAddress,
          password: values.password,
          // Country is not included for ReZEN agents
          teamInvitationId: !isRevokedInvite
            ? teamInvitationId || undefined
            : undefined,
          genericTeamInvitationId: genericTeamInvitationId || undefined,
        });

        // Login after successful signup
        const loginRequest: LoginRequest = {
          usernameOrEmail: values.emailAddress,
          password: values.password,
        };

        const { data } = await new AuthControllerApi(
          await getKeymakerConfiguration(),
        ).authenticateUser(loginRequest);

        // Set auth cookie and navigate based on user state
        setAuthCookie(data?.accessToken!);
        if (data?.userBlockedUntilEmailVerified) {
          navigate('/agent-verify-email');
        } else if (!!data?.forceMfa) {
          navigate('/agent-enable-mfa');
        } else {
          await dispatch(fetchAuthUserDetail());
          navigate('/');
        }
      } catch (error: any) {
        Logger.error('Unable to sign up', error);
        ErrorService.notifyIgnoreHandled('Unable to sign up', error);
        setSubmitError(getCommonsApiErrorMessage(error));
        // Sign out the user if signup succeeds but onboarding fails
        await dispatch(signOutUser());
      } finally {
        setLoading(false);
        isSubmittingRef.current = false;
      }
    },
    [
      dispatch,
      navigate,
      teamInvitationId,
      genericTeamInvitationId,
      isRevokedInvite,
    ],
  );

  // Navigate to login - memoized to prevent recreation
  const navigateToLogin = useCallback(() => {
    navigate('/agent-login');
  }, [navigate]);

  // Secondary action configuration - memoized to prevent recreation
  const secondaryAction = useMemo(
    () => ({
      text: 'Already have an account? Log In',
      onClick: navigateToLogin,
      icon: faChevronRight,
    }),
    [navigateToLogin],
  );

  // Validation refinements - memoized to prevent recreation
  const refinements = useMemo(
    () => ({
      usernameRefinement: checkUsernameAvailability,
      emailRefinement: checkEmailAvailability,
    }),
    [checkUsernameAvailability, checkEmailAvailability],
  );

  // Default values from query params - memoized
  const defaultValues = useMemo(() => {
    const values: Record<string, any> = {};
    if (firstName) values.firstName = firstName;
    if (lastName) values.lastName = lastName;
    if (onboardingEmail) values.emailAddress = onboardingEmail;
    return values;
  }, [firstName, lastName, onboardingEmail]);

  // Field configuration with dynamic disabled state - memoized
  const fieldConfig = useMemo(
    () => ({
      ...BASE_FIELD_CONFIG,
      email: {
        ...BASE_FIELD_CONFIG.email,
        disabled: !!onboardingEmail,
      },
    }),
    [onboardingEmail],
  );

  if (fetchingInvitation) {
    return (
      <div className='h-screen'>
        <DefaultSplashV2 />
      </div>
    );
  }

  return (
    <HomeRoute title='One Real: Registration - ReZEN Agents'>
      <GenericSignUpForm
        fields={FORM_FIELDS}
        fieldConfig={fieldConfig}
        refinements={refinements}
        onSubmit={handleSubmit}
        loading={loading}
        submitError={submitError}
        submitButtonText='Create Account'
        submitButtonIcon={faChevronRight}
        title='Create Account'
        secondaryAction={secondaryAction}
        layout={LAYOUT_CONFIG}
        showRightImage
        defaultValues={defaultValues}
      />
    </HomeRoute>
  );
};

export default RezenAgentSignUpRoute;
